import { createFileRoute, Link } from "@tanstack/react-router";
import { breadcrumbJsonLd } from "@/lib/breadcrumb-jsonld";
import { serviceJsonLd } from "@/lib/service-jsonld";
import { faqPageJsonLd, type FaqItem } from "@/lib/faq-jsonld";
import { FaqAccordion } from "@/components/site/FaqAccordion";
import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";
import { QuoteRequestForm } from "@/components/site/QuoteRequestForm";
import {
  CheckCircle2,
  ShieldCheck,
  Leaf,
  Award,
  MapPin,
  ArrowRight,
  Phone,
} from "lucide-react";

const SITE_URL = "https://elvion-group.nl";

export const Route = createFileRoute("/offerte/camerabewaking")({
  head: () => {
    const url = `${SITE_URL}/offerte/camerabewaking`;
    const title = "Camerabewaking laten installeren | Offerte in 24 uur — Elvion Group Nederland";
    const description =
      "Camerabewaking thuis of zakelijk laten installeren. All-inclusive: advies, montage, bekabeling. 10 jaar garantie, offerte op maat binnen 24 uur.";
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:url", content: url },
        { property: "og:type", content: "website" },
        { property: "og:locale", content: "nl_NL" },
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        breadcrumbJsonLd([
          { name: "Offerte", path: "/offerte" },
          { name: "Camerabewaking", path: "/offerte/camerabewaking" },
        ]),
        serviceJsonLd({
          path: "/offerte/camerabewaking",
          name: "Camerabewaking laten installeren",
          description:
            "All-inclusive installatie van IP-camerasystemen (Hikvision, Dahua) voor woning en bedrijf: advies, montage, bekabeling en app-koppeling. 10 jaar garantie, offerte binnen 24 uur.",
          serviceType: "Camerabewaking",
          offerCatalog: [
            "IP-camerasystemen 4K",
            "Nachtzicht & ColorVu",
            "NVR & opslag",
            "Hik-Connect / DMSS koppeling",
            "Zakelijke camerabewaking",
            "Onderhoud en service",
          ],
        }),
        faqPageJsonLd(FAQ_ITEMS, `${SITE_URL}/offerte/camerabewaking#faq`),
      ],
    };
  },
  component: CamerabewakingOffertePage,
});

const FAQ_ITEMS: FaqItem[] = [
  {
    q: "Hoeveel kost het laten installeren van camerabewaking?",
    a: "De prijs hangt af van het aantal camera's, de gewenste kwaliteit (2MP, 4MP of 8MP) en of er nieuwe bekabeling nodig is. Via onze configurator ziet u direct een indicatie; de definitieve prijs staat in uw offerte binnen 24 uur.",
  },
  {
    q: "Welke merken camera's gebruiken jullie?",
    a: "Wij werken standaard met Hikvision en Dahua — twee A-merken met bewezen betrouwbaarheid, nachtzicht en app-koppeling via Hik-Connect of DMSS.",
  },
  {
    q: "Is de bekabeling zichtbaar na installatie?",
    a: "Nee. Onze monteurs werken de UTP/PoE-bekabeling zo veel mogelijk uit het zicht weg — via kruipruimte, zolder, kabelgoten of langs bestaande leidingen.",
  },
  {
    q: "Kan ik de beelden op mijn telefoon bekijken?",
    a: "Ja. Elk systeem wordt gekoppeld aan de gratis app (Hik-Connect of DMSS), zodat u live beelden en opnamen kunt terugkijken op smartphone, tablet of pc.",
  },
  {
    q: "Hoe lang worden de beelden bewaard?",
    a: "Standaard leveren wij een NVR met voldoende opslag voor circa 14-30 dagen aan opnamen. Meer opslag is optioneel bij te bestellen.",
  },
  {
    q: "Hoeveel garantie krijg ik op de installatie?",
    a: "U krijgt 2 jaar fabrieksgarantie op de apparatuur en garantie op de installatie door onze eigen erkende monteurs.",
  },
  {
    q: "Voldoen jullie installaties aan de AVG-regels?",
    a: "Ja. Wij adviseren u over camerapositionering en zichtbare stickerplaatsing zodat de installatie voldoet aan de AVG-vereisten voor woning of bedrijf.",
  },
];

const TRUST_STRIP = [
  { icon: ShieldCheck, label: "2 jaar fabrieksgarantie" },
  { icon: Leaf, label: "A-merken (Dahua, Hikvision)" },
  { icon: Award, label: "Erkende monteurs" },
  { icon: MapPin, label: "Landelijk beschikbaar" },
];

const USPS = [
  "Advies op maat voor jouw woning of pand",
  "All-inclusief: advies, montage en installatie",
  "Onzichtbare bekabeling — netjes weggewerkt",
  "Offerte op maat binnen 24 uur",
];

function CamerabewakingOffertePage() {
  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Header />
      <main className="flex-1">
        {/* Trust strip */}
        <div className="border-b border-border bg-foreground text-background">
          <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-between gap-4 px-4 py-3 text-sm sm:px-6 lg:px-8">
            <a href="tel:0850806695" className="flex items-center gap-2 font-semibold hover:text-orange">
              <Phone className="h-4 w-4" />
              085 080 66 95
            </a>
            <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
              {TRUST_STRIP.map((t) => (
                <div key={t.label} className="flex items-center gap-1.5">
                  <t.icon className="h-4 w-4 text-orange" />
                  <span>{t.label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Hero with form */}
        <section className="relative overflow-hidden bg-gradient-to-br from-[oklch(0.25_0.04_240)] via-[oklch(0.22_0.05_250)] to-[oklch(0.18_0.04_260)] text-white">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,oklch(0.45_0.18_240/0.4),transparent_60%)]" />
          <div className="relative mx-auto grid max-w-7xl gap-12 px-4 py-14 sm:px-6 lg:grid-cols-[1fr_minmax(380px,440px)] lg:gap-16 lg:px-8 lg:py-20">
            <div>

              <h1 className="text-4xl font-extrabold leading-tight tracking-tight md:text-5xl lg:text-6xl">
                Camerabewaking thuis
                <br />
                laten installeren
              </h1>
              <p className="mt-5 max-w-xl text-base text-white/80 md:text-lg">
                Een veilig huis of bedrijfspand begint bij goede camerabewaking.
                Onze monteurs adviseren, leveren en installeren een
                camerasysteem op maat — inclusief app op je telefoon.
              </p>

              <ul className="mt-8 space-y-3 text-base md:text-lg">
                {USPS.map((u) => (
                  <li key={u} className="flex items-start gap-3">
                    <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-orange" />
                    <span>{u}</span>
                  </li>
                ))}
              </ul>

              <div className="mt-8">
                <Link
                  to="/cam-configurator"
                  className="inline-flex items-center gap-2 text-sm font-semibold text-white underline-offset-4 hover:underline"
                >
                  Of stel je eigen camerasysteem samen in de configurator
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            </div>

            <div className="lg:pt-2">
              <QuoteRequestForm
                service="camerabewaking"
                title="Offerte op maat binnen 24 uur"
                subtitle="Vul je gegevens in. We bellen je terug voor een gratis advies."
              />
            </div>
          </div>
        </section>

        {/* Info section */}
        <section className="mx-auto max-w-7xl px-4 py-14 sm:px-6 md:py-20 lg:px-8">
          <div className="grid gap-10 lg:grid-cols-3">
            <div>
              <h2 className="text-2xl font-bold text-foreground">Welk systeem past?</h2>
              <p className="mt-3 text-muted-foreground">
                Dome, Turret, Bullet of PTZ — wij adviseren je over de juiste
                vorm, het aantal camera's en de beeldkwaliteit (Full HD t/m 4K),
                afgestemd op je woning of bedrijfspand.
              </p>
            </div>
            <div>
              <h2 className="text-2xl font-bold text-foreground">Wat is inbegrepen?</h2>
              <ul className="mt-3 space-y-2 text-foreground">
                {[
                  "Inmeetbezoek en advies op locatie",
                  "Levering, montage en aansluiting",
                  "Netwerkkabels en montagebeugels",
                  "App-configuratie op je telefoon",
                  "2 jaar fabrieksgarantie + servicedesk",
                ].map((i) => (
                  <li key={i} className="flex items-start gap-2 text-sm">
                    <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-orange" />
                    {i}
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h2 className="text-2xl font-bold text-foreground">Wat kost het?</h2>
              <p className="mt-3 text-muted-foreground">
                Een compleet camerasysteem (4 camera's + recorder + installatie)
                start vanaf <strong>€ 1.295</strong>. Voor een exacte prijs
                gebruik je de configurator of vraag je hierboven een offerte op
                maat aan.
              </p>
              <Link
                to="/cam-configurator"
                className="mt-4 inline-flex items-center gap-2 rounded-md border border-border bg-background px-4 py-2 text-sm font-semibold text-foreground hover:bg-muted"
              >
                Open de configurator
                <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
        </section>

        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <FaqAccordion
            id="camerabewaking-faq"
            title="Veelgestelde vragen over camerabewaking"
            intro="Alles over prijs, installatie, app-koppeling, opslag en AVG-conformiteit."
            items={FAQ_ITEMS}
          />
        </div>
      </main>
      <Footer />
    </div>
  );
}
